/* Variant and gallery UI only. The theme's existing product-form owns cart,
   checkout, tracking and error handling. No duplicate add-to-cart request. */
(() => {
  if (customElements.get('jp-eyewear-story')) return;
  class OneswearStory extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); }
      catch { return; }
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.controls = [...this.querySelectorAll('[data-option-index]')];
      const packs = this.querySelector('.os-packs');
      if (packs) [...packs.querySelectorAll('.os-pack')].sort((a,b)=>Number(a.dataset.pairCount)-Number(b.dataset.pairCount)).forEach(label=>packs.append(label));
      this.mainImage = this.querySelector('.os-product-photo img');
      this.input = this.form?.querySelector('[name="id"]');
      this.submit = this.form?.querySelector('[name="add"]');
      this.quantity = this.querySelector('input[name="quantity"]');
      this.label = this.submit?.querySelector('.main-atc__label__text');
      this.onChange = event => {
        if (event.target.closest('[data-option-index]')) this.update(true);
        if (event.target === this.quantity) this.clampQuantity();
      };
      this.onClick = event => {
        const packLink = event.target.closest('[data-pick-pack]');
        if (packLink) {
          const control = this.querySelector('[data-option-index="0"]');
          const input = [...(control?.querySelectorAll('input') || [])].find(i=>i.value===packLink.dataset.pickPack);
          if (input && !input.disabled) { input.checked=true; this.update(true); }
        }
        const photo = event.target.closest('[data-photo]');
        if (photo) {
          this.showImage(photo.dataset.photo, photo.dataset.alt);
          this.querySelectorAll('[data-photo]').forEach(button => button.setAttribute('aria-pressed', String(button === photo)));
        }
        const quantityButton = event.target.closest('[data-quantity-step]');
        if (quantityButton && this.quantity) {
          const step = Number(this.quantity.step) || 1;
          this.quantity.value = String((Number(this.quantity.value) || Number(this.quantity.min) || 1) + step * Number(quantityButton.dataset.quantityStep));
          this.clampQuantity();
          this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      this.onPopState = () => {
        const variant = this.variants.find(item => String(item.id) === new URL(location.href).searchParams.get('variant'));
        if (variant) { this.setOptions(variant.options); this.update(false); }
      };
      this.onSubmit = event => {
        this.clampQuantity();
        if (!this.currentVariant?.available || !this.form.reportValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
      if (this.form && this.input && this.submit && this.variants.length) {
        this.form.addEventListener('submit', this.onSubmit, true);
        const initial = this.variants.find(item => String(item.id) === new URL(location.href).searchParams.get('variant')) || this.variants.find(item => String(item.id) === this.input.value) || this.variants[0];
        this.setOptions(initial.options);
        this.update(false);
      }
      this.sticky = this.querySelector('.os-sticky');
      if (this.sticky) {
        this.onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.sticky.hidden = this.querySelector('.os-offer').getBoundingClientRect().bottom >= 0;
          });
        };
        window.addEventListener('scroll', this.onScroll, {passive: true});
        window.addEventListener('resize', this.onScroll, {passive: true});
        this.onScroll();
      }
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopState);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.initialized = false;
    }
    selectedOptions() {
      const values = [];
      this.controls.forEach(control => { values[Number(control.dataset.optionIndex)] = control.querySelector('input:checked')?.value; });
      return values;
    }
    setOptions(values) {
      this.controls.forEach(control => control.querySelectorAll('input').forEach(input => { input.checked = input.value === values[Number(control.dataset.optionIndex)]; }));
    }
    update(changeUrl) {
      const options = this.controls.length ? this.selectedOptions() : (this.currentVariant?.options || this.variants[0]?.options || []);
      const variant = this.variants.find(item => item.options.every((value, index) => value === options[index]));
      this.currentVariant = variant;
      const available = Boolean(variant?.available);
      this.input.value = variant ? String(variant.id) : '';
      this.input.disabled = !variant;
      this.submit.disabled = !available;
      this.submit.setAttribute('aria-disabled', String(!available));
      if (!this.label) {
        const container = this.submit.querySelector('.main-atc__label');
        if (container) { container.textContent = ''; this.label = document.createElement('span'); this.label.className = 'main-atc__label__text'; container.append(this.label); }
      }
      if (this.label) this.label.textContent = available ? this.dataset.atc : (variant ? this.dataset.soldOut : this.dataset.unavailable);
      this.querySelector('[data-price]').textContent = variant ? this.plainMoney(variant.money) : '—';
      const hasSaving = Boolean(variant && variant.compare_at_price > variant.price);
      const compare = this.querySelector('[data-compare]');
      compare.hidden = !hasSaving;
      compare.textContent = variant ? this.plainMoney(variant.compare_money) : '';
      const saving = this.querySelector('[data-saving]');
      saving.hidden = !hasSaving;
      saving.textContent = hasSaving ? `SAVE ${Math.round(100 * (variant.compare_at_price - variant.price) / variant.compare_at_price)}%` : '';
      const warning = this.querySelector('[data-variant-warning]');
      warning.hidden = available;
      warning.textContent = available ? '' : (variant ? this.dataset.soldOut : this.dataset.unavailable);
      const summary = this.querySelector('[data-selected-summary]');
      if (summary) summary.textContent = variant ? variant.options.join(' · ') : this.dataset.unavailable;
      this.controls.forEach(control => {
        const index = Number(control.dataset.optionIndex);
        control.querySelectorAll('input').forEach(input => {
          const candidate = this.variants.find(item => item.options.every((value, i) => value === (i === index ? input.value : options[i])));
          input.disabled = !candidate || !candidate.available;
          const label = input.closest('.os-option');
          const price = label.querySelector('[data-option-price]');
          const original = label.querySelector('[data-option-compare]');
          const unit = label.querySelector('[data-unit-price]');
          if (unit) unit.textContent = candidate?.units > 0 ? `${this.plainMoney(candidate.unit_money)} / pair` : '';
          if (price) price.textContent = candidate ? this.plainMoney(candidate.money) : '—';
          if (original) { original.textContent = candidate ? this.plainMoney(candidate.compare_money) : ''; original.hidden = !(candidate && candidate.compare_at_price > candidate.price); }
        });
      });
      this.querySelectorAll('[data-bundle-value]').forEach(card=>{
        const candidate = this.variants.find(item=>item.options[0]===card.dataset.bundleValue && item.options.slice(1).every((value,i)=>value===options[i+1]));
        card.querySelector('[data-bundle-price]').textContent=candidate?this.plainMoney(candidate.money):'—';
        card.querySelector('[data-bundle-unit]').textContent=candidate?.units>0?`${this.plainMoney(candidate.unit_money)} per pair`:'';
        card.querySelector('[data-pick-pack]').setAttribute('aria-disabled',String(!candidate?.available));
      });
      if (variant) {
        this.quantity.min = String(variant.min || 1);
        this.quantity.step = String(variant.increment || 1);
        if (variant.max != null) this.quantity.max = String(variant.max);
        else this.quantity.removeAttribute('max');
        this.clampQuantity();
        const price = this.querySelector('[data-sticky-price]');
        const title = this.querySelector('[data-sticky-variant]');
        if (price) price.textContent = this.plainMoney(variant.money);
        if (title) title.textContent = variant.options.join(' / ');
        if (variant.image) { this.showImage(variant.image, variant.options.join(' / ')); this.querySelectorAll('[data-photo]').forEach(button => button.setAttribute('aria-pressed','false')); }
        if (changeUrl) { const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url); }
      }
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.dispatchEvent(new CustomEvent('oneswear:variant-change', { bubbles: true, detail: { variant } }));
    }
    clampQuantity() {
      if (!this.quantity) return;
      const min = Number(this.quantity.min) || 1;
      const step = Number(this.quantity.step) || 1;
      const max = this.quantity.hasAttribute('max') ? Number(this.quantity.max) : Infinity;
      let value = Math.floor(Number(this.quantity.value));
      if (!Number.isFinite(value)) value = min;
      value = Math.max(min, Math.min(value, max));
      value = min + Math.floor((value - min) / step) * step;
      this.quantity.value = String(Math.max(min, value));
    }
    plainMoney(html) { const template = document.createElement('template'); template.innerHTML = html || ''; return template.content.textContent; }
    showImage(src, alt) { if (!this.mainImage) return; this.mainImage.removeAttribute('srcset'); this.mainImage.src = src; this.mainImage.alt = alt || ''; }
  }
  customElements.define('jp-eyewear-story', OneswearStory);
})();
